import React from 'react'

import {
  WorkspaceLogo,
  Truncate,
  pluralize,
  ProjectLogo,
  ListRowTitleText,
  ListRowBoldTitleText,
  ListRowSubtitleText,
} from '@sketch/components'
import PermissionWrapper from '../../modals/ShareSettingsModal/PermissionWrapper'
import ProjectMembershipsTooltip from '../../../projects/components/ProjectMembershipsTooltip'

// GraphQL
import {
  ProjectFragment,
  useGetProjectMembershipsQuery,
  useGetProjectMembershipsCountQuery,
  PublicWorkspaceFragment,
} from '@sketch/gql-types'

export interface ProjectRowProps {
  topLevelProject: Pick<
    ProjectFragment,
    'identifier' | 'name' | 'workspaceAccessLevel'
  >
  workspace: Pick<PublicWorkspaceFragment, 'identifier' | 'name' | 'avatar'>
  actions: React.ReactNode
  className?: string
}

export const ProjectRow = ({
  topLevelProject,
  workspace,
  actions,
  className,
}: ProjectRowProps) => {
  const isProjectAvailableToWorkspace =
    topLevelProject.workspaceAccessLevel === 'DEFAULT'

  const { data, loading, error } = useGetProjectMembershipsQuery({
    variables: {
      projectIdentifier: topLevelProject.identifier,
    },
    skip: isProjectAvailableToWorkspace,
  })

  const projectIcon =
    topLevelProject.workspaceAccessLevel === 'DEFAULT' ? (
      <WorkspaceLogo
        src={workspace.avatar?.small}
        workspaceName={workspace.name}
        size="32px"
      />
    ) : (
      <ProjectLogo />
    )

  const membersCount = data?.project.members?.meta.totalCount

  return (
    <PermissionWrapper
      key={topLevelProject.identifier}
      className={className}
      aria-label={`Folder row of ${topLevelProject.name}`}
    >
      {projectIcon}
      <Truncate>
        <ListRowTitleText aria-label={`from ${topLevelProject.name} folder`}>
          from{' '}
          <ListRowBoldTitleText>{topLevelProject.name}</ListRowBoldTitleText>{' '}
          folder
        </ListRowTitleText>
        <ProjectMembershipsTooltip
          workspace={workspace}
          memberships={data?.project.members}
          workspaceAccessLevel={topLevelProject.workspaceAccessLevel}
          shouldShowWorkspace={false}
          disabled={
            loading ||
            Boolean(error) ||
            isProjectAvailableToWorkspace ||
            membersCount === 0
          }
        >
          <ProjectCountSummary
            projectIdentifier={topLevelProject.identifier}
            isRestricted={!isProjectAvailableToWorkspace}
          />
        </ProjectMembershipsTooltip>
      </Truncate>
      {actions}
    </PermissionWrapper>
  )
}

interface ProjectCountSummaryProps {
  projectIdentifier: string
  isRestricted: boolean
}

function ProjectCountSummary(props: ProjectCountSummaryProps) {
  const { projectIdentifier, isRestricted } = props

  const {
    data: countData,
    loading: countLoading,
    error: countError,
  } = useGetProjectMembershipsCountQuery({
    variables: {
      projectIdentifier,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  if (countLoading || !countData || countError) {
    return null
  }

  const allCounts = countData.project.membershipCount

  const memberCount = allCounts.admin + allCounts.member
  const memberText = pluralize('Folder Member', 'Folder Members', memberCount)

  const groupCount = allCounts.group
  const groupText = pluralize('Group', 'Groups', groupCount)

  const isRestrictedTextParts = [
    memberCount,
    memberText,
    ...(groupCount > 0 ? ['and', groupCount, groupText] : []),
  ]

  const guestCount = allCounts.guest
  const guestText = pluralize('Guest', 'Guests', guestCount)

  let text = isRestricted
    ? isRestrictedTextParts.join(' ')
    : `All Workspace Members`

  if (guestCount) {
    text += ` + ${guestCount} ${guestText}`
  }

  return <ListRowSubtitleText>{text}</ListRowSubtitleText>
}
